import { Injectable, NgZone, OnDestroy, inject } from '@angular/core';
import * as Sentry from '@sentry/angular';
import { Subject } from 'rxjs';

export type SwBridgeMessage =
  | { type: 'SW_RETRY_STARTED'; url: string }
  | { type: 'SW_RECOVERED'; url: string; attempts: number }
  | { type: 'SW_RESOURCE_FAILED'; url: string; status?: number; attempts?: number; message?: string }
  | { type: 'SW_RETRY_THRESHOLD'; count: number; message?: string };

@Injectable({ providedIn: 'root' })
export class SwBridgeService implements OnDestroy {
  private ngZone = inject(NgZone);

  public messages$ = new Subject<SwBridgeMessage>();

  private _listener = (ev: MessageEvent) => {
    // eslint-disable-next-line angular/module-getter
    this.ngZone.run(() => {
      this.handleMessage(ev.data);
    });
  };

  ngOnDestroy(): void {
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.removeEventListener('message', this._listener);
    }
    this.messages$.complete();
  }

  init() {
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener('message', this._listener);
    }
  }

  private handleMessage(data: any) {
    if (!data || !data.type) return;

    switch (data.type) {
      case 'SW_RETRY_STARTED': {
        const payload: SwBridgeMessage = {
          type: 'SW_RETRY_STARTED',
          url: data.url,
        };

        this.messages$.next(payload);

        try {
          const eventId = Sentry.captureMessage('Static resource retry started', {
            level: 'info',
            tags: {
              source: 'service-worker',
              resource_url: data.url,
            },
          });

          // eslint-disable-next-line no-console
          console.info('[SENTRY] Retry started sent:', eventId, data.url);
        } catch (err) {
          console.error('[SENTRY] Failed to send retry started:', err);
        }

        break;
      }

      case 'SW_RECOVERED': {
        const payload: SwBridgeMessage = {
          type: 'SW_RECOVERED',
          url: data.url,
          attempts: data.attempts,
        };

        this.messages$.next(payload);

        try {
          const eventId = Sentry.captureMessage('Static resource recovered after retry', {
            level: 'info',
            tags: {
              source: 'service-worker',
              resource_url: data.url,
            },
            extra: { attempts: data.attempts },
          });

          // eslint-disable-next-line no-console
          console.info('[SENTRY] Recovery sent:', eventId, data.url);
        } catch (err) {
          console.error('[SENTRY] Failed to send recovery:', err);
        }

        break;
      }

      case 'SW_RESOURCE_FAILED': {
        const payload: SwBridgeMessage = {
          type: 'SW_RESOURCE_FAILED',
          url: data.url,
          status: data.status,
          attempts: data.attempts,
          message: data.message,
        };

        this.messages$.next(payload);

        try {
          const err = new Error(`Static resource failed: ${data.url} status=${data.status}`);

          const eventId = Sentry.captureException(err, {
            tags: {
              source: 'service-worker',
              resource_url: data.url,
              status: String(data.status),
            },
            extra: {
              attempts: data.attempts,
              message: data.message,
            },
          });

          // eslint-disable-next-line no-console
          console.info('[SENTRY] Resource failed sent:', eventId, data.url);
        } catch (err) {
          console.error('[SENTRY] Failed to send resource failed:', err);
        }

        break;
      }

      default:
        break;
    }
  }
}
